import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const dividerVariants = cva('shrink-0 bg-gray-200 dark:bg-gray-700', {
  variants: {
    orientation: {
      horizontal: 'h-px w-full',
      vertical: 'w-px self-stretch',
    },
    spacing: {
      none: '',
      sm: 'my-2',
      md: 'my-4',
      lg: 'my-6',
    },
  },
  compoundVariants: [
    { orientation: 'vertical', spacing: 'sm', className: 'mx-2 my-0' },
    { orientation: 'vertical', spacing: 'md', className: 'mx-4 my-0' },
    { orientation: 'vertical', spacing: 'lg', className: 'mx-6 my-0' },
  ],
  defaultVariants: { orientation: 'horizontal', spacing: 'none' },
})

export interface DividerProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof dividerVariants> {
  label?: React.ReactNode
}

export const Divider = React.forwardRef<HTMLDivElement, DividerProps>(
  ({ className, orientation, spacing, label, ...rest }, ref) => {
    if (label && orientation !== 'vertical') {
      return (
        <div
          ref={ref}
          role="separator"
          aria-orientation="horizontal"
          className={cn(
            'flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400',
            className
          )}
          {...rest}
        >
          <div
            className={cn(
              dividerVariants({ orientation: 'horizontal', spacing: 'none' })
            )}
          />
          <span className="shrink-0">{label}</span>
          <div
            className={cn(
              dividerVariants({ orientation: 'horizontal', spacing: 'none' })
            )}
          />
        </div>
      )
    }
    return (
      <div
        ref={ref}
        role="separator"
        aria-orientation={orientation ?? 'horizontal'}
        className={cn(dividerVariants({ orientation, spacing, className }))}
        {...rest}
      />
    )
  }
)

Divider.displayName = 'Divider'

export { dividerVariants }
